/**
 * 编辑表格
 * 组件定位：在antd Table组件之上，实现表格筛选和表格表单的能力，如涉及业务组件功能，请在SPTableWithModel 中实现
 */
import React, { useEffect, forwardRef, useImperativeHandle, useRef, useMemo, useState } from 'react';
import { Form, Table, Empty, Space, Alert, Checkbox } from 'antd';
import { SPTableProps } from './types';
import Search, { SearchHandle } from './Search';
import _, { divide } from 'lodash';
import { useDebounceFn, useDeepCompareEffect, useSetState } from 'ahooks';
import useColums from './effects/useColumns';
import { useRowDataValidate } from './effects/useRowDataValidate';
import './index.less';


function EditableTable(props: SPTableProps, ref) {
  const [form] = Form.useForm<any>();
  const searchRef = useRef<SearchHandle>(null);
  const {
    tableBaseMode,
    rowKey,
    value = [],
    columns = [],
    pagination,
    optionColumns,
    onTableChange,
    scroll,
    toolBarRender,
    onChange,
    showTableIndex,
    rowSelection,
    search,
    onSearch,
    searchParams,
    onRow,
    loading,
    title,
    footer,
    onValidateChange,
  } = props;
  // 内部临时字段标识，将[] 转化为 { _data: [] }，否则antd表格可能出现数据异常
  const DataKey = '_data';
  // 基于搜索排序生成搜索项
  const searchColums = _.sortBy(columns.filter((x) => x.search !== false), 'searchOptions.sequence');
  // 生成列项
  const tableColums = columns.filter((x) => x.visible !== false);
  // 顶部按钮render函数
  const toolbarBtns = toolBarRender ? toolBarRender() : [];
  const [state, setState] = useSetState<{
    /** 是否显示实时的全局校验 */
    isShowRealtimeValidate: boolean;
    /** 只显示错误行 */
    onlyShowErrorRow: boolean;
    /** 临时记录错误的行index */
    onlyShowRowIndexs: number[];
  }>({
    isShowRealtimeValidate: false,
    onlyShowErrorRow: false,
    onlyShowRowIndexs: [],
  });
  // 是否显示错误行获取全量行
  let dataSource = value;
  if (state.onlyShowErrorRow) {
    dataSource = dataSource.filter((row: any, index: number) => state.onlyShowRowIndexs.includes(index));
  }

  const { run: onChangeFn } = useDebounceFn(onChange, { wait: 500 });
  const _onChange = (values: any) => {
    // 不在表单里的数据会丢失，包括RECORD 顾重新合并
    onChangeFn && onChangeFn(values[DataKey]);
  };

  const formInstance = {
    getFieldError: (key: string | string[]) => {
      return form.getFieldError(Array.isArray(key) ? [DataKey, ...key] : [DataKey, key]);
    },
    getFieldsError: () => {
      return form.getFieldsError();
    },
    getFieldsValue: () => {
      return value;
      // return form.getFieldsValue()[DataKey] || {};
    },
    getFieldValue: (key: string | string[]) => {
      return form.getFieldValue(Array.isArray(key) ? [DataKey, ...key] : [DataKey, key]);
    },
    setFieldValue: (key: string | string[], v: any) => {
      // const value = form.getFieldsValue()[DataKey];
      // _.set(value, key, v);
      // onChangeFn(form.getFieldsValue()[DataKey]);
      const formValue = form.getFieldsValue()[DataKey];
      const newValue = _.merge([], value, formValue);
      _.set(newValue, key, v);
      onChangeFn(newValue);
    },
    setFieldsValue: (v: any) => {
      if (_.isObject(v)) {
        // 不在当前页的数据可能查询不到，能能返回 [{_id: 11}, { _id: 22}],
        const formValue = form.getFieldsValue()[DataKey]; // 有部分数据通过setFields设置的
        // 补全行数据未在表单中的
        const newValue = (formValue || []).map((row, index) => {
          return Object.assign({}, value[index], row);
        });

        Object.keys(v).forEach((k: any) => {
          _.set(newValue, [k], { ...newValue[k], ...v[k] });
        });
        form.setFieldsValue({ [DataKey]: newValue });
        onChangeFn(newValue);
      }
      // onChangeFn(form.getFieldsValue()[DataKey]);
    },
  };

  const _columns = useColums({
    value,
    columns: tableColums,
    tableBaseMode,
    optionColumns,
    form: formInstance,
    showTableIndex,
    onChange: _onChange,
    rowKey,
    dataKey: DataKey,
    current: pagination ? pagination.current : 1,
    pageSize: pagination ? pagination.pageSize : 10,
  });

  const {
    validateResult,
    validateRows,
  } = useRowDataValidate({
    value,
    columns: tableColums as any,
    formRef: form,
    isShowRealtimeValidate: state.isShowRealtimeValidate,
  });

  // 全部行数据校验
  const validateFields = async () => {
    // 执行过校验后实时显示错误信息
    setState({ isShowRealtimeValidate: true });
    // 当前页表单校验，并标红
    await form.validateFields();
    // 所有数据校验，包含非当前页
    await validateRows();
    return null;
  };

  /** table分页具体处理 */
  const pga = useMemo(() => {
    if (!pagination) {
      return false;
    }
    const paginationInfo = {
      ...pagination,
      total: pagination.total || dataSource.length, // 计算真实的行数，否则内部数据过滤后可能和外部传入的total不一致
      showTotal: (total, range) => `第 ${range[0]}-${range[1]} 条/总共 ${total} 条`,
    };

    return paginationInfo;
  }, [pagination, tableBaseMode, dataSource]);

  useDeepCompareEffect(() => {
    form.setFieldsValue({ [DataKey]: value });
  }, [value]);

  useImperativeHandle(ref, () => ({
    validateFields,
    getSearchParams: () => searchRef.current?.getFieldsValue(),
    removeRow: (index: number) => {
      const newValue = value.filter((x, i) => i !== index);
      // form.setFieldValue(DataKey, []);
      onChange(newValue);
    },
    removeRowById: (id: string | string[]) => {
      const ids = _.isArray(id) ? id : [id];
      const newValue = value.filter((x) => !ids.includes(x._id));
      onChange(newValue);
    },
    // resetFields: (value: any) => form.resetFields({ [DataKey]: value }),
  }));

  useEffect(() => {
    onValidateChange && onValidateChange(validateResult);
  }, [validateResult]);


  // 分页变更时，或者行数据新增删除时，显示错误行信息
  useEffect(() => {
    if (state.isShowRealtimeValidate) {
      setImmediate(() => {
        form.validateFields();
      });
    }
  }, [JSON.stringify(pagination), value.length]);

  // 在只显示错误行的状态下，如果添加行数据，则需要将添加的数据行显示出来
  useEffect(() => {
    const onlyShowRowIndexs = validateResult ? validateResult.map(x => x.rowIndex) : [];
    setState({ onlyShowRowIndexs: _.uniq([...state.onlyShowRowIndexs, ...onlyShowRowIndexs]) });
  }, [value.length, validateResult]);


  // 勾选显示错误行
  const OnlyShowErrorRowCheck = (
    <Checkbox
      checked={state.onlyShowErrorRow}
      onChange={(e) => {
        const { checked } = e.target;
        // 勾选时记录当前错误行
        const onlyShowRowIndexs = validateResult ? validateResult.map(x => x.rowIndex) : [];
        setState({ onlyShowErrorRow: checked, onlyShowRowIndexs });
        // 显示所有行的错误信息
        setImmediate(() => {
          form.validateFields();
        });
      }}
    >
      只显示错误行
    </Checkbox>
  );

  return (
    <div className="sp-editable-table">
      {(title || toolbarBtns) &&
        <div className="lcp-editable-table-head">
          {/* <h1>{title}</h1> */}
          <div className="sp-editable-table-header-btns" style={{ marginBottom: 8 }}>
            <Space>{toolbarBtns}</Space>
          </div>
        </div>
      }
      {state.isShowRealtimeValidate && validateResult && value.length > 0 &&
        <>
          {validateResult.length > 0 &&
            <Alert
              message={`有 ${validateResult.length} 行数据问题，请按照提示修改。`}
              type="error"
              action={OnlyShowErrorRowCheck}
              showIcon
              style={{ marginBottom: 4, lineHeight: '24px' }}
            />}
          {validateResult.length === 0 &&
            <Alert
              message={'恭喜！全部修改完成。'}
              type="success"
              action={OnlyShowErrorRowCheck}
              showIcon
              style={{ marginBottom: 4, lineHeight: '24px' }}
            />}
        </>
      }
      {search &&
        <Search
          value={searchParams}
          columns={searchColums}
          onSearch={(params: any) => {
            onSearch && onSearch(params);
          }}
          onReset={() => {
            onSearch && onSearch({});
          }}
          ref={searchRef}
        />
      }
      <Form
        form={form}
        onValuesChange={(changedValues, allValues) => {
          _onChange(allValues);
        }}
        labelWrap
        key={value.length}
      >
        {/* 表单的每行的id数据需要在表单中保存下来 */}
        <div style={{ display: 'none' }}>
          {value.map((v: any, index: number) => <Form.Item name={[DataKey, index, rowKey]} key={index + _.get(DataKey, index, rowKey)} />)}
        </div>
        {/* <VirtualTable columns={_columns} dataSource={value} scroll={{ y: 600, x: '100vw' }} /> */}
        <Table
          size="small"
          scroll={scroll}
          rowKey={rowKey}
          columns={_columns}
          dataSource={dataSource}
          pagination={pga}
          onRow={onRow}
          onChange={(...arg) => {
            // setCurrent(arg[0].current);
            onTableChange && onTableChange(...arg);
          }}
          rowSelection={rowSelection}
          locale={{
            emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={loading ? '加载中' : '暂无数据'} />,
          }}
          loading={loading}
        />
        {_.isFunction(footer) && <>{footer()}</>}
      </Form>
    </div>
  );
}

export default forwardRef(EditableTable);
